import { weekFileName } from '../lib/weekFileName';
import { DocLangToggle } from './ui/doc-lang-toggle';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from './ui/dialog';
import { Plus, Pencil, Copy, Trash2, Play, Square, Clock, Download, AlertCircle, FileText, Receipt, Camera, MapPin, Wrench, PenLine, LayoutGrid, Rows3, Table2, ChevronDown, ChevronRight, Columns3, Users } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuCheckboxItem, DropdownMenuTrigger, DropdownMenuLabel } from './ui/dropdown-menu';
import ScrollX from './shared/ScrollX';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import i18n from '../i18n';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import EntityPhotoGallery from './EntityPhotoGallery';
import SignaturePad from './ui/SignaturePad';
import { useDefaultSignature } from '../hooks/useDefaultSignature';
import { enqueue as enqueueOffline } from '../lib/offlineQueue';
import { loadDocs, createDoc, saveDoc, isLocalId } from '../lib/offlineDocs';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { OfflineNotice, PendingSyncBadge } from './OfflineNotice';

const AUX_CACHE_KEY = 'expertfix-task-aux';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_OPTIONS = [
  { value: 'not_started', label: i18n.t('auto.lbl.notStarted'), color: 'bg-gray-100 text-gray-700' },
  { value: 'in_progress', label: i18n.t('auto.lbl.inProgress2'), color: 'bg-blue-100 text-blue-700' },
  { value: 'blocked', label: i18n.t('auto.lbl.blocked'), color: 'bg-amber-100 text-amber-800' },
  { value: 'completed', label: i18n.t('auto.lbl.completed'), color: 'bg-green-100 text-green-700' },
];

const CATEGORY_OPTIONS = [
  { value: 'standard', label: i18n.t('auto.lbl.standard'), color: 'bg-gray-100 text-gray-700' },
  { value: 'extra', label: i18n.t('auto.lbl.extra'), color: 'bg-orange-100 text-orange-700' },
  { value: 'rework', label: i18n.t('auto.lbl.rework'), color: 'bg-red-100 text-red-700' },
];

const PRIORITY_OPTIONS = [
  { value: 'none',      label: i18n.t('auto.lbl.none'),      color: 'bg-gray-100 text-gray-500' },
  { value: 'low',       label: i18n.t('auto.lbl.low'),       color: 'bg-slate-100 text-slate-700' },
  { value: 'normal',    label: i18n.t('auto.lbl.normal'),    color: 'bg-blue-100 text-blue-700' },
  { value: 'high',      label: i18n.t('auto.lbl.high'),      color: 'bg-amber-100 text-amber-800' },
  { value: 'immediate', label: i18n.t('auto.lbl.immediate'), color: 'bg-red-100 text-red-700 font-semibold' },
];

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : '');

// Three ways to read the same list: roomy cards, compact expandable rows,
// or the full table. The table is the only one that can out-grow a laptop
// screen, so it gets the column chooser.
const TASK_VIEWS = ['cards', 'compact', 'table'];
const TASK_VIEW_KEY = 'view:project-tasks';
const TASK_COLS_KEY = 'cols:project-tasks';
const TASK_COLUMNS = [
  { key: 'trade', label: i18n.t('auto.lbl.trade') },
  { key: 'priority', label: i18n.t('auto.lbl.priority') },
  { key: 'planned', label: i18n.t('auto.lbl.planned') },
  { key: 'qty', label: i18n.t('auto.lbl.qty') },
  { key: 'variance', label: i18n.t('auto.lbl.variance') },
  { key: 'active', label: i18n.t('auto.lbl.liveTimers') },
];
const readTaskView = () => {
  try {
    const v = localStorage.getItem(TASK_VIEW_KEY);
    return TASK_VIEWS.includes(v) ? v : 'table';
  } catch { return 'table'; }
};
const readTaskCols = () => {
  const all = Object.fromEntries(TASK_COLUMNS.map((c) => [c.key, true]));
  try {
    const raw = JSON.parse(localStorage.getItem(TASK_COLS_KEY) || '{}');
    return { ...all, ...raw };
  } catch { return all; }
};

// Task planned/actual times are WALL-CLOCK at the job site — they are
// stored as naive strings ("YYYY-MM-DDTHH:MM[:SS]") and rendered as-is,
// so an engineer in Germany who types 07:00 sees 07:00 in every view
// (PDF, Excel, browser) regardless of who's looking. NO timezone math.
import { SearchSortBar, matchesQuery, byCreated } from './ListSearchSort';
import { overlapsWeek } from '../lib/weekFilter';
import { useSavedView } from '../hooks/useSavedView';
import { FloatingSaveButton } from './FloatingSaveButton';
import useDeepShortcut from '../hooks/useDeepShortcut';
import { dateLocale } from '../lib/dateFormat';
import { DateText } from './ui/week-badge';

const toLocalInput = (iso) => {
  if (!iso) return '';
  // Accept "YYYY-MM-DDTHH:MM[:SS][.fff][Z|±HH:MM]" — strip any TZ suffix
  // for legacy records that were saved with offsets before this change.
  const s = String(iso);
  const m = s.match(/^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2})/);
  return m ? m[1] : '';
};
const fromLocalInput = (val) => (val ? `${val}:00` : null);

// "1.5" → "1h 30m", "0.25" → "15m", "2" → "2h"
// "1.5" → "1h 30m", "0.25" → "15m", "-7.25" → "−7h 15m". Always shows
// minutes when there are minutes; "0" returns "0m".
const formatHM = (h) => {
  const n = Number(h) || 0;
  const sign = n < 0 ? '−' : '';
  const totalMin = Math.round(Math.abs(n) * 60);
  const hh = Math.floor(totalMin / 60);
  const mm = totalMin % 60;
  if (hh && mm) return `${sign}${hh}h ${mm}m`;
  if (hh) return `${sign}${hh}h`;
  return `${sign}${mm}m`;
};
// Signed variant for variance — "0h 0m" rendered as "0m" still, but a
// positive value gets a leading "+" so the sign is unambiguous next to
// a negative one.
const formatHMSigned = (h) => {
  const n = Number(h) || 0;
  if (n === 0) return '±0m';
  return (n > 0 ? '+' : '') + formatHM(n);
};

const ProjectTasks = ({ projectId, projectName, project }) => {
  const { t } = useTranslation();
  // Task currently being marked complete; when non-null the
  // completion-signature dialog is open. Kept here (rather than a
  // dedicated inline state) so parent list refresh + timer changes
  // still work while the dialog waits for the operator.
  const [completingTask, setCompletingTask] = useState(null);
  const { user } = useAuth();
  const { can, isSuperadmin } = usePermissions();
  const canBillExtras = isSuperadmin || can('tasks.bill_extras');
  const [tasks, setTasks] = useState([]);
  // task_id -> [{ id, report_number, severity }]: built once per project
  // load so each task row can show a "Repair for DR-X" badge without
  // its own request.
  const [damageRefsByTaskId, setDamageRefsByTaskId] = useState({});
  const [templates, setTemplates] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [trades, setTrades] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});
  const [filterCategory, setFilterCategory] = useState('');
  const [filterTrade, setFilterTrade] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterDateFrom, setFilterDateFrom] = useState('');
  const [filterDateTo, setFilterDateTo] = useState('');
  const { search, setSearch, sortBy, setSortBy } = useSavedView('project-tasks');
  const [week, setWeek] = useState(null);  const [selected, setSelected] = useState(new Set());
  const [view, setView] = useState(readTaskView);
  const [cols, setCols] = useState(readTaskCols);
  const [expandedTasks, setExpandedTasks] = useState(() => new Set());
  const setTaskView = (v) => {
    setView(v);
    try { localStorage.setItem(TASK_VIEW_KEY, v); } catch { /* private mode */ }
  };
  const toggleCol = (key) => setCols((prev) => {
    const next = { ...prev, [key]: !prev[key] };
    try { localStorage.setItem(TASK_COLS_KEY, JSON.stringify(next)); } catch { /* private mode */ }
    return next;
  });
  const toggleExpanded = (id) => setExpandedTasks((prev) => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    return n;
  });
  const [timeOpen, setTimeOpen] = useState(null); // task object
  const [entries, setEntries] = useState([]);
  const [entryForm, setEntryForm] = useState({});
  const [billOpen, setBillOpen] = useState(false);
  const [billSummary, setBillSummary] = useState(null);
  const [billForm, setBillForm] = useState({});
  const [photosOpen, setPhotosOpen] = useState(null); // task object
  const [withPhotosExport, setWithPhotosExport] = useState(false);
  const [offlineList, setOfflineList] = useState(false);
  const isOnline = useOnlineStatus();

  const load = useCallback(async () => {
    // Tasks themselves go through the offline layer so the list keeps
    // working (and offline-created tasks stay visible) with no signal.
    try {
      const { rows, offline } = await loadDocs('task', projectId, async () => (
        (await axios.get(`${API}/projects/${projectId}/tasks`)).data
      ));
      setTasks(rows || []);
      setOfflineList(offline);
    } catch (e) {
      toast.error(t('common.errorGeneric'));
    }
    // Supporting lists are reference data — mirrored in localStorage so
    // the New Task dialog still works offline.
    try {
      const [tpl, emp, settings, dmg] = await Promise.all([
        axios.get(`${API}/task-templates`),
        axios.get(`${API}/employees`),
        axios.get(`${API}/settings`),
        axios.get(`${API}/project-damage-reports?project_id=${projectId}`).catch(() => ({ data: [] })),
      ]);
      const templatesList = (tpl.data || []).filter((x) => !x.is_archived);
      const employeeList = (emp.data || []).filter((x) => x.is_active !== false);
      const tradeList = settings.data?.task_trades || [];
      setTemplates(templatesList);
      setEmployees(employeeList);
      setTrades(tradeList);
      try {
        localStorage.setItem(AUX_CACHE_KEY, JSON.stringify({
          templates: templatesList, employees: employeeList, trades: tradeList,
        }));
      } catch { /* quota */ }
      const map = {};
      for (const r of (dmg.data || [])) {
        for (const tid of (r.linked_task_ids || [])) {
          (map[tid] ||= []).push({
            id: r.id, report_number: r.report_number, severity: r.severity, status: r.status,
          });
        }
      }
      setDamageRefsByTaskId(map);
    } catch (_e) {
      try {
        const aux = JSON.parse(localStorage.getItem(AUX_CACHE_KEY) || '{}');
        setTemplates(aux.templates || []);
        setEmployees(aux.employees || []);
        setTrades(aux.trades || []);
      } catch { /* nothing cached */ }
    }
  }, [projectId]);

  useEffect(() => { if (projectId) load(); }, [projectId, load]);

  // Refresh the task list only when the photo count actually moved, and
  // keep the callback identity stable so the gallery never re-fetches in a
  // loop. `photosOpen` is read through a ref for the same reason.
  const photosOpenRef = useRef(null);
  photosOpenRef.current = photosOpen;
  const onPhotoCountChange = useCallback((count) => {
    const current = photosOpenRef.current?.photo_count || 0;
    if (count !== current) load();
  }, [load]);

  const empByName = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e.name || e.full_name || 'Unknown'])), [employees]);

  // Only team members assigned to this project — used for picking an
  // employee when logging time. Falls back to all active employees if
  // the project hasn't loaded its team yet (avoids an empty dropdown).
  const teamEmployees = useMemo(() => {
    const ids = new Set((project?.team_members || []).map((m) => m.employee_id).filter(Boolean));
    if (!ids.size) return employees;
    return employees.filter((e) => ids.has(e.id));
  }, [employees, project]);

  // Dashboard shortcuts: pin the open task, or the New task form.
  useDeepShortcut({
    active: open && !!editing,
    tab: 'projects',
    context: { openProjectId: projectId, projectTab: 'tasks', focusTaskId: editing?.id },
    label: editing?.name,
    subtitle: projectName || '',
    icon: 'tasks',
    perms: ['projects.view'],
    priority: 3,
    focusKey: 'focusTaskId',
    onFocus: (id) => {
      const found = tasks.find((x) => x.id === id);
      if (found) openEdit(found);
    },
  });
  useDeepShortcut({
    active: open && !editing,
    tab: 'projects',
    context: { openProjectId: projectId, projectTab: 'tasks', create: 'task' },
    label: t('shortcuts.qc.task'),
    subtitle: projectName || '',
    icon: 'tasks',
    perms: ['projects.view'],
    priority: 4,
    focusKey: 'create',
    onFocus: (v) => { if (v === 'task') openNew(); },
  });

  const openNew = () => {
    setEditing(null);    setForm({
      project_id: projectId,
      template_id: '',
      name: '', description: '', location: '', trade: '',
      category: 'standard', est_hours_unit: 0, est_workers: 1, quantity: 1,
      priority: 'normal',
      planned_start: '', planned_end: '',
    });
    setOpen(true);
  };

  // "Copy & edit" — open the New Task dialog pre-filled with the source
  // task's definition fields. Skips state (status), schedule (planned
  // start/end) and any actuals — those belong to the original task.
  const copyTask = (t) => {
    setEditing(null);
    setForm({
      project_id: projectId,
      template_id: '',
      name: `${t.name || ''} (copy)`,
      description: t.description || '',
      location: t.location || '',
      trade: t.trade || '',
      category: t.category || 'standard',
      // Use the per-unit value — the backend recomputes the total
      est_hours_unit: t.est_hours_unit ?? ((t.est_hours || 0) / (t.quantity || 1)),
      est_workers: t.est_workers || 1,
      quantity: t.quantity || 1,
      priority: t.priority || 'normal',
      planned_start: '', planned_end: '',
    });
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);    setForm({
      name: t.name, description: t.description || '', location: t.location || '', trade: t.trade || '',
      category: t.category || 'standard',
      est_hours_unit: t.est_hours_unit ?? ((t.est_hours || 0) / (t.quantity || 1)),
      est_workers: t.est_workers || 1, quantity: t.quantity || 1,
      priority: t.priority || 'normal',
      status: t.status || 'not_started', notes: t.notes || '',
      planned_start: toLocalInput(t.planned_start), planned_end: toLocalInput(t.planned_end),
    });
    setOpen(true);
  };

  const onTemplatePick = (templateId) => {
    const tpl = templates.find((t) => t.id === templateId);
    if (!tpl) return setForm((f) => ({ ...f, template_id: '' }));
    setForm((f) => ({
      ...f,
      template_id: tpl.id,
      name: f.name || tpl.name,
      description: f.description || tpl.description || '',
      trade: f.trade || tpl.trade || '',
      category: tpl.category || 'standard',
      est_hours_unit: tpl.est_hours || 0,
      est_workers: tpl.est_workers || 1,
    }));
  };

  const save = async () => {
    if (!form.name?.trim()) { toast.error(t('auto.proj.taskNameRequired')); return; }
    try {
      if (editing) {
        const body = {
          name: form.name, description: form.description || null, location: form.location || null, trade: form.trade || null,
          category: form.category, quantity: parseFloat(form.quantity) || 1,
          est_hours_unit: parseFloat(form.est_hours_unit) || 0, est_workers: parseInt(form.est_workers) || 1,
          priority: form.priority || 'normal', status: form.status, notes: form.notes || null,
          planned_start: fromLocalInput(form.planned_start),
          planned_end: fromLocalInput(form.planned_end),
        };
        await saveDoc({
          kind: 'task', id: editing.id, body, doc: editing,
          label: `Task ${form.name || editing.name || ''}`.trim(),
          onlineSave: async () => (await axios.put(`${API}/project-tasks/${editing.id}`, body)).data,
        });
      } else {
        const body = {
          project_id: projectId,
          template_id: form.template_id || null,
          name: form.name, description: form.description || null, location: form.location || null, trade: form.trade || null,
          category: form.category, quantity: parseFloat(form.quantity) || 1,
          est_hours_unit: parseFloat(form.est_hours_unit) || 0, est_workers: parseInt(form.est_workers) || 1,
          priority: form.priority || 'normal',
          planned_start: fromLocalInput(form.planned_start),
          planned_end: fromLocalInput(form.planned_end),
        };
        await createDoc({
          kind: 'task', projectId, payload: body,
          docShell: { status: 'not_started' },
          label: `Task — ${body.name}`,
          onlineCreate: async () => (await axios.post(`${API}/project-tasks`, body)).data,
        });
      }
      toast.success(editing ? t('tasks.toasts.updated') : t('tasks.toasts.created'));
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    }
  };

  const delTask = async (task) => {
    if (!window.confirm(`Delete task "${task.name}" and all its time entries?`)) return;
    await axios.delete(`${API}/project-tasks/${task.id}`);
    toast.success(t('tasks.toasts.deleted'));
    load();
  };

  const cycleStatus = async (tItem) => {
    const order = ['not_started', 'in_progress', 'blocked', 'completed'];
    const idx = order.indexOf(tItem.status || 'not_started');
    const next = order[(idx + 1) % order.length];
    // Transitioning INTO "completed" pops the signature dialog so
    // the operator can leave a handwritten sign-off before the
    // status update commits. The actual PUT happens inside
    // handleTaskCompletionSign / handleTaskCompletionSkip.
    if (next === 'completed') {
      setCompletingTask(tItem);
      return;
    }
    try {
      await saveDoc({
        kind: 'task', id: tItem.id, body: { status: next }, doc: tItem,
        label: `Task ${tItem.name || ''}`.trim(),
        onlineSave: async () => (await axios.put(`${API}/project-tasks/${tItem.id}`, { status: next })).data,
      });
      load();
    } catch (e) {
      toast.error(t('auto.proj.failedToChangeStatus'));
    }
  };

  // Complete a task with an optional handwritten signature. Passing
  // ``signature`` = null just marks the task complete without a
  // captured signature (kept for operators who prefer typed sign-off).
  const finalizeTaskCompletion = async (task, signature) => {
    try {
      const body = { status: 'completed' };
      if (signature) {
        body.completion_signature_image = signature;
        body.completion_signed_by = user?.full_name || user?.username || '';
        body.completion_signed_at = new Date().toISOString();
      }
      const { online } = await saveDoc({
        kind: 'task', id: task.id, body, doc: task,
        label: `Task completion — ${task.name || ''}`.trim(),
        onlineSave: async () => (await axios.put(`${API}/project-tasks/${task.id}`, body)).data,
      });
      toast.success(online
        ? t('tasks.toasts.completed')
        : 'Signed on this device — it will upload when you\'re back online');
      setCompletingTask(null);
      load();
    } catch (_e) {
      toast.error(t('auto.proj.failedToCompleteTask'));
    }
  };

  // NB: never name the task parameter `t` here — it shadows the i18n `t()`
  // and the success toast then throws *after* the timer has already started,
  // which is exactly how "Failed to start" appeared on a working timer.
  const startTimer = async (task, employee_id) => {
    try {
      await axios.post(`${API}/timer/start`, { task_id: task.id, employee_id });
      toast.success(t('tasks.toasts.startedTimer'));
      window.dispatchEvent(new CustomEvent('timer:changed'));
      load();
      if (timeOpen?.id === task.id) loadEntries(task.id);
    } catch (e) { toast.error(t('auto.proj.failedToStart')); }
  };

  const stopTimer = async (task, employee_id) => {
    try {
      await axios.post(`${API}/timer/stop`, { task_id: task.id, employee_id });
      toast.success(t('tasks.toasts.stoppedTimer'));
      window.dispatchEvent(new CustomEvent('timer:changed'));
      load();
      if (timeOpen?.id === task.id) loadEntries(task.id);
    } catch (e) { toast.error(t('auto.proj.failedToStop')); }
  };

  const loadEntries = async (taskId) => {
    const r = await axios.get(`${API}/project-tasks/${taskId}/time-entries`);
    setEntries(r.data || []);
  };

  const openTimeFor = (t) => {
    setTimeOpen(t);
    setEntryForm({ employee_id: '', hours: '', work_date: new Date().toISOString().slice(0, 10), notes: '', multiply: false });
    loadEntries(t.id);
  };

  const addManualEntry = async () => {
    if (!entryForm.employee_id || !entryForm.hours) { toast.error(t('auto.proj.employeeHoursRequired')); return; }
    const qty = entryForm.multiply ? (parseFloat(timeOpen?.quantity) || 1) : 1;
    const payload = {
      task_id: timeOpen.id,
      employee_id: entryForm.employee_id,
      hours: parseFloat(entryForm.hours),
      quantity: qty,
      work_date: new Date(entryForm.work_date).toISOString(),
      notes: entryForm.notes || null,
    };
    // Offline-first: if we know we're offline, queue immediately and
    // skip the doomed network round-trip. Otherwise attempt the POST
    // and fall back to the queue on any network error (server
    // unreachable, request timed out, etc). 4xx responses from a
    // reachable server are surfaced as errors — those aren't
    // connectivity issues and re-queueing them would just spin.
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      try {
        await enqueueOffline({
          url: `${API}/time-entries`,
          method: 'POST',
          body: payload,
          token: axios.defaults.headers?.common?.Authorization?.replace(/^Bearer\s+/i, '') || null,
          label: `${entryForm.hours}h · ${timeOpen.name || 'task'}`,
        });
        toast.success(t('auto.proj.hoursSavedOffline'));
        setEntryForm(prev => ({ ...prev, hours: '', notes: ''  }));
      } catch { toast.error(t('auto.proj.failedToSaveOffline')); }
      return;
    }
    try {
      await axios.post(`${API}/time-entries`, payload);
      toast.success(t('auto.proj.hoursLogged'));
      setEntryForm(prev => ({ ...prev, hours: '', notes: ''  }));
      loadEntries(timeOpen.id);
      load();
    } catch (e) {
      // Network error → queue for later. `e.response` is undefined on
      // pure network failures; anything with a response came back
      // from the server and is a real 4xx/5xx we should surface.
      if (!e?.response) {
        try {
          await enqueueOffline({
            url: `${API}/time-entries`,
            method: 'POST',
            body: payload,
            token: axios.defaults.headers?.common?.Authorization?.replace(/^Bearer\s+/i, '') || null,
            label: `${entryForm.hours}h · ${timeOpen.name || 'task'}`,
          });
          toast.success(t('auto.proj.hoursSavedOffline'));
          setEntryForm(prev => ({ ...prev, hours: '', notes: ''  }));
          return;
        } catch { /* fall through to error toast */ }
      }
      toast.error(t('auto.proj.failedToLog'));
    }
  };

  const delEntry = async (id) => {
    if (!window.confirm(t('auto.proj.deleteThisTimeEntry'))) return;
    await axios.delete(`${API}/time-entries/${id}`);
    loadEntries(timeOpen.id);
    load();
  };

  const exportTasks = async (format = 'xlsx') => {
    try {
      const params = { project_id: projectId, sort_by: sortBy };
      if (filterCategory) params.category = filterCategory;
      if (filterTrade) params.trade = filterTrade;
      if (filterLocation) params.location = filterLocation;
      if (filterEmployee) params.employee_id = filterEmployee;
      if (filterDateFrom) params.date_from = new Date(filterDateFrom).toISOString();
      if (filterDateTo) params.date_to = new Date(filterDateTo).toISOString();
      if (selected.size > 0) params.ids = Array.from(selected).join(',');
      if (withPhotosExport) params.with_photos = true;
      const url = format === 'pdf' ? 'task-reports/export/pdf' : 'task-reports/export/excel';
      const r = await axios.get(`${API}/${url}`, { params, responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a');
      a.href = blobUrl;
      const ext = format === 'pdf' ? 'pdf' : 'xlsx';
      const fn = `tasks_${(projectName || 'project').replace(/\W+/g, '_')}${filterCategory ? `_${filterCategory}` : ''}${withPhotosExport ? '_with_photos' : ''}.${ext}`;
      a.download = weekFileName(fn);
      a.click();
      window.URL.revokeObjectURL(blobUrl);
      toast.success(`Exported ${selected.size > 0 ? `${selected.size} selected ` : ''}task(s)`);
    } catch { toast.error(t('auto.proj.exportFailed')); }
  };

  const openBillExtras = async () => {
    try {
      const r = await axios.get(`${API}/projects/${projectId}/extra-billing-summary`);
      setBillSummary(r.data);
      setBillForm({
        issue_date: new Date().toISOString().slice(0, 10),
        sale_date: new Date().toISOString().slice(0, 10),
        due_date: new Date(Date.now() + 14 * 86400000).toISOString().slice(0, 10),
        notes: '',
        task_ids: new Set((r.data?.tasks || []).map((x) => x.task_id)),
      });
      setBillOpen(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load extras');
    }
  };

  const submitBillExtras = async () => {
    if (!billSummary) return;
    if (!billSummary.hourly_rate || billSummary.hourly_rate <= 0) {
      toast.error(t('auto.proj.projectHourlyRateMustBeSet'));
      return;
    }
    if (!billForm.task_ids || billForm.task_ids.size === 0) {
      toast.error(t('auto.proj.selectAtLeastOneTaskTo'));
      return;
    }
    try {
      const r = await axios.post(`${API}/projects/${projectId}/bill-extras`, {
        issue_date: billForm.issue_date ? new Date(billForm.issue_date).toISOString() : null,
        sale_date: billForm.sale_date ? new Date(billForm.sale_date).toISOString() : null,
        due_date: billForm.due_date ? new Date(billForm.due_date).toISOString() : null,
        notes: billForm.notes || null,
        task_ids: Array.from(billForm.task_ids),
      });
      toast.success(`Invoice ${r.data.invoice_number || ''} created (${r.data.n_lines} line${r.data.n_lines > 1 ? 's' : ''})`);
      setBillOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Billing failed');
    }
  };

  // ---- Filtering & sorting (client-side; mirrors backend export semantics) ----
  const filtered = useMemo(() => {
    let list = tasks;
    if (filterCategory) list = list.filter((t) => t.category === filterCategory);
    if (filterTrade) list = list.filter((t) => (t.trade || '').toLowerCase() === filterTrade.toLowerCase());
    if (filterLocation) list = list.filter((t) => (t.location || '').toLowerCase() === filterLocation.toLowerCase());
    if (filterEmployee) {
      list = list.filter((t) => (t.workers || []).includes(filterEmployee) ||
        (t.active_timers || []).some((a) => a.employee_id === filterEmployee));
    }
    if (filterDateFrom || filterDateTo) {
      const from = filterDateFrom ? new Date(filterDateFrom).getTime() : -Infinity;
      const to = filterDateTo ? new Date(filterDateTo).getTime() + 86400000 : Infinity;
      list = list.filter((t) => {
        const ps = t.planned_start ? new Date(t.planned_start).getTime() : null;
        const pe = t.planned_end ? new Date(t.planned_end).getTime() : null;
        const le = t.last_entry_at ? new Date(t.last_entry_at).getTime() : null;
        const candidate = ps || pe || le;
        return candidate == null ? !filterDateFrom && !filterDateTo : candidate >= from && candidate <= to;
      });
    }
    if (week) list = list.filter((t) => overlapsWeek(week, t.planned_start || t.last_entry_at || t.created_at, t.planned_end || t.last_entry_at));
    if (search.trim()) {
      list = list.filter((t) => matchesQuery(search, [
        t.name, t.description, t.trade, t.category, t.location, t.notes,
        t.priority, t.status, t.task_number,
        ...(t.workers || []).map((id) => empByName[id]),
      ]));
    }
    // Same priority? Older planned_start wins. Tasks with no planned_start
    // fall to the bottom of the priority bucket. Final tiebreak: name.
    const FAR_FUTURE = 8.64e15; // ~Date max millis — any real date is "older"
    const startMs = (t) => (t.planned_start ? new Date(t.planned_start).getTime() : FAR_FUTURE);
    const PRIO_RANK = { immediate: 4, high: 3, normal: 2, low: 1, none: 0 };
    const cmp = {
      created_desc: byCreated('desc'),
      created_asc: byCreated('asc'),
      priority: (a, b) =>
        (PRIO_RANK[b.priority || 'normal'] - PRIO_RANK[a.priority || 'normal'])
        || (startMs(a) - startMs(b))
        || (a.name || '').localeCompare(b.name || ''),
      trade: (a, b) => (a.trade || '').localeCompare(b.trade || '') || (a.name || '').localeCompare(b.name || ''),
      name: (a, b) => (a.name || '').localeCompare(b.name || ''),
      category: (a, b) => (a.category || '').localeCompare(b.category || ''),
      planned_start: (a, b) => startMs(a) - startMs(b),
      actual_hours: (a, b) => (b.actual_hours || 0) - (a.actual_hours || 0),
      variance: (a, b) => ((b.actual_hours || 0) - (b.est_hours || 0)) - ((a.actual_hours || 0) - (a.est_hours || 0)),
    };
    return [...list].sort(cmp[sortBy] || cmp.created_desc);
  }, [tasks, filterCategory, filterTrade, filterLocation, filterEmployee, filterDateFrom, filterDateTo, sortBy, search, week]);

  // Distinct locations present on this project's tasks, alphabetised — drives
  // the Location filter dropdown so users only see options that exist.
  const locationOptions = useMemo(() => {
    const set = new Set();
    for (const t of tasks) {
      if (t.location) set.add(t.location);
    }
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [tasks]);

  // Reset selection if filtered list changes
  useEffect(() => {
    setSelected((prev) => {
      const allowed = new Set(filtered.map((t) => t.id));
      const next = new Set();
      prev.forEach((id) => { if (allowed.has(id)) next.add(id); });
      return next;
    });
  }, [filtered]);

  const allSelected = filtered.length > 0 && filtered.every((t) => selected.has(t.id));
  const someSelected = filtered.some((t) => selected.has(t.id));
  const toggleAll = () => {
    if (allSelected) setSelected(new Set());
    else setSelected(new Set(filtered.map((t) => t.id)));
  };
  const toggleOne = (id) => {
    setSelected((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };

  const totals = useMemo(() => {
    const est = filtered.reduce((s, t) => s + (t.est_hours || 0), 0);
    const act = filtered.reduce((s, t) => s + (t.actual_hours || 0), 0);
    return { est, act, variance: act - est, count: filtered.length };
  }, [filtered]);

  // Wall-clock display — read the naive ISO string directly so a time
  // recorded as "07:00 at the job site" shows as 07:00 to every viewer.
  const fmtDt = (iso) => {
    if (!iso) return '—';
    const s = String(iso);
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
    if (!m) return s;
    return `${m[3]}.${m[2]}.${m[1]} ${m[4]}:${m[5]}`;
  };

  return (
    <div className="space-y-4" data-testid={`project-tasks-${projectId}`}>
      <OfflineNotice
        isOnline={isOnline}
        cached={offlineList}
        what="tasks"
        testId="tasks-offline-notice"
      />
      {/* Filter toolbar */}
      <Card><CardContent className="p-3 space-y-3">
        <SearchSortBar
          search={search}
          onSearch={setSearch}
          sortBy={sortBy}
          onSort={setSortBy}
          placeholder={t('auto.proj.searchTasksByNameDescriptionTrade')}
          testIdPrefix="project-tasks"
          week={week}
          onWeek={setWeek}
          options={[
            { value: 'created_desc', label: t('auto.lbl.newestFirst') },
            { value: 'created_asc', label: t('auto.lbl.oldestFirst') },
            { value: 'priority', label: t('auto.lbl.priority') },
            { value: 'trade', label: t('auto.lbl.trade') },
            { value: 'planned_start', label: t('auto.lbl.plannedStart') },
            { value: 'name', label: t('auto.lbl.taskName') },
            { value: 'category', label: t('auto.lbl.category') },
            { value: 'actual_hours', label: t('auto.lbl.actualHoursDesc') },
            { value: 'variance', label: t('auto.lbl.varianceDesc') },
          ]}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2 items-end">
          <div>
            <Label className="text-xs h-5 flex items-center">{t('auto.proj.category')}</Label>
            <Select value={filterCategory || '__all__'} onValueChange={(v) => setFilterCategory(v === '__all__' ? '' : v)}>
              <SelectTrigger data-testid="task-category-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t('auto.proj.allCategories')}</SelectItem>
                {CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs h-5 flex items-center">{t('auto.proj.trade')}</Label>
            <Select value={filterTrade || '__all__'} onValueChange={(v) => setFilterTrade(v === '__all__' ? '' : v)}>
              <SelectTrigger data-testid="task-trade-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t('auto.proj.allTrades')}</SelectItem>
                {trades.map((t) => <SelectItem key={t} value={t}>{cap(t)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs h-5 flex items-center gap-1"><MapPin className="h-3 w-3" />{t('auto.proj.location')}</Label>
            <Select value={filterLocation || '__all__'} onValueChange={(v) => setFilterLocation(v === '__all__' ? '' : v)} disabled={locationOptions.length === 0}>
              <SelectTrigger data-testid="task-location-filter"><SelectValue placeholder={locationOptions.length === 0 ? t('auto.proj.noLocations') : t('auto.proj.all')} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t('auto.proj.allLocations')}</SelectItem>
                {locationOptions.map((loc) => <SelectItem key={loc} value={loc}>{loc}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs h-5 flex items-center">{t('auto.proj.employee')}</Label>
            <Select value={filterEmployee || '__all__'} onValueChange={(v) => setFilterEmployee(v === '__all__' ? '' : v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">{t('auto.proj.allEmployees')}</SelectItem>
                {employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name || e.full_name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs h-5 flex items-center">{t('auto.proj.dateFrom')}</Label>
            <Input type="date" className="min-w-0 pr-2" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} />
          </div>
          <div>
            <Label className="text-xs h-5 flex items-center">{t('auto.proj.dateTo')}</Label>
            <Input type="date" className="min-w-0 pr-2" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} />
          </div>
        </div>
        <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2 sm:justify-between sm:items-center">
          <div className="grid grid-cols-1 sm:flex sm:flex-wrap gap-2">
            <DocLangToggle testId="doc-lang-projecttasks-0" />
            <Button variant="outline" size="sm" onClick={() => exportTasks('xlsx')} data-testid="export-tasks-xlsx-btn">
              <Download className="h-4 w-4 mr-1" />Export Excel{selected.size > 0 ? ` (${selected.size})` : ''}
            </Button>
            <Button variant="outline" size="sm" onClick={() => exportTasks('pdf')} data-testid="export-tasks-pdf-btn">
              <FileText className="h-4 w-4 mr-1" />Export PDF{selected.size > 0 ? ` (${selected.size})` : ''}
            </Button>
            <label className="flex items-center gap-1 text-xs text-gray-600 px-2 py-1 border rounded cursor-pointer">
              <Checkbox checked={withPhotosExport} onCheckedChange={(v) => setWithPhotosExport(!!v)} data-testid="export-with-photos-toggle" />
              <Camera className="h-3.5 w-3.5" />{t('auto.proj.includePhotosPdfExcel')}
            </label>
            {canBillExtras && (
              <Button variant="outline" size="sm" onClick={openBillExtras}
                className="text-[#AA0000] border-[#AA0000] hover:bg-[#AA0000] hover:text-white"
                data-testid="bill-extras-btn">
                <Receipt className="h-4 w-4 mr-1" />{t('auto.proj.billExtrasToCustomer')}
              </Button>
            )}
          </div>
          <Button onClick={openNew} className="bg-[#AA0000] hover:bg-[#880000]" data-testid="new-project-task-btn">
            <Plus className="h-4 w-4 mr-1" />{t('tasks.addTask')}
          </Button>
        </div>
      </CardContent></Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat label={t('auto.proj.tasks')} value={totals.count} />
        <Stat label={t('auto.proj.estHours')} value={formatHM(totals.est)} />
        <Stat label={t('auto.proj.actualHours')} value={formatHM(totals.act)} />
        <Stat label={t('auto.proj.variance')} value={formatHMSigned(totals.variance)}
              valueClass={totals.variance > 0 ? 'text-[#AA0000]' : totals.variance < 0 ? 'text-green-700' : ''} />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs text-gray-500">{filtered.length} task(s)</div>
        <div className="flex items-center gap-2">
          {view === 'table' && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm" data-testid="task-columns-btn">
                  <Columns3 className="h-4 w-4 mr-1" />{t('auto.proj.columns')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>{t('auto.proj.showColumns')}</DropdownMenuLabel>
                {TASK_COLUMNS.map((c) => (
                  <DropdownMenuCheckboxItem
                    key={c.key}
                    checked={!!cols[c.key]}
                    onCheckedChange={() => toggleCol(c.key)}
                    onSelect={(e) => e.preventDefault()}
                    data-testid={`task-col-toggle-${c.key}`}
                  >
                    {c.label}
                  </DropdownMenuCheckboxItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          <div className="inline-flex rounded-md border bg-white overflow-hidden" data-testid="task-view-toggle">
            {[
              { v: 'cards', Icon: LayoutGrid, title: t('auto.lbl.cards') },
              { v: 'compact', Icon: Rows3, title: t('auto.lbl.compactRows') },
              { v: 'table', Icon: Table2, title: t('auto.lbl.table') },
            ].map(({ v, Icon, title }) => (
              <Button
                key={v}
                type="button"
                size="sm"
                variant={view === v ? 'default' : 'ghost'}
                className={`rounded-none h-9 px-3 ${view === v ? 'bg-[#AA0000] hover:bg-[#880000]' : ''}`}
                onClick={() => setTaskView(v)}
                title={title}
                data-testid={`task-view-${v}`}
              >
                <Icon className="h-4 w-4" />
              </Button>
            ))}
          </div>
        </div>
      </div>

      {filtered.length === 0 && (
        <Card><CardContent className="p-6 text-center text-gray-500">{t('auto.proj.noTasksAddOneToGet')}</CardContent></Card>
      )}

      {/* ── Cards: every field visible, nothing to scroll sideways ── */}
      {view === 'cards' && filtered.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-3" data-testid="tasks-cards-view">
          {filtered.map((tItem) => {
            const variance = (tItem.actual_hours || 0) - (tItem.est_hours || 0);
            const status = STATUS_OPTIONS.find((s) => s.value === tItem.status) || STATUS_OPTIONS[0];
            const cat = CATEGORY_OPTIONS.find((c) => c.value === tItem.category) || CATEGORY_OPTIONS[0];
            const prio = PRIORITY_OPTIONS.find((x) => x.value === (tItem.priority || 'normal')) || PRIORITY_OPTIONS[2];
            const checked = selected.has(tItem.id);
            return (
              <Card key={tItem.id} className={checked ? 'border-[#AA0000]' : ''} data-testid={`task-card-${tItem.id}`}>
                <CardContent className="p-3 space-y-2">
                  <div className="flex items-start gap-2">
                    <Checkbox checked={checked} onCheckedChange={() => toggleOne(tItem.id)}
                      aria-label={`Select ${tItem.name}`} data-testid={`task-select-${tItem.id}`} />
                    <button type="button" className="flex-1 min-w-0 text-left" onClick={() => openEdit(tItem)}
                      data-testid={`task-card-open-${tItem.id}`}>
                      <div className="font-medium break-words">{tItem.name}</div>
                      {tItem.description && <div className="text-xs text-gray-500 break-words">{tItem.description}</div>}
                    </button>
                    <button onClick={() => cycleStatus(tItem)} title={t('auto.proj.clickToCycleStatus')}
                      className={`shrink-0 px-1.5 py-0.5 rounded text-xs ${status.color}`}
                      data-testid={`task-status-cycle-${tItem.id}`}>{status.label}</button>
                  </div>
                  <div className="flex flex-wrap gap-1 text-xs">
                    <span className={`px-1.5 py-0.5 rounded ${cat.color}`}>{cat.label}</span>
                    <span className={`px-1.5 py-0.5 rounded ${prio.color}`}>{prio.label}</span>
                    {tItem.trade && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">{cap(tItem.trade)}</span>}
                    {(tItem.quantity || 1) !== 1 && <span className="px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">×{tItem.quantity}</span>}
                  </div>
                  {tItem.location && (
                    <div className="text-xs text-[#AA0000] font-medium flex items-center gap-1">
                      <MapPin className="h-3 w-3 shrink-0" /><span className="break-words">{tItem.location}</span>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div><div className="text-gray-500">{t('auto.proj.est')}</div><div className="font-medium tabular-nums">{formatHM(tItem.est_hours)}</div></div>
                    <div><div className="text-gray-500">{t('auto.proj.actual')}</div><div className="font-medium tabular-nums">{formatHM(tItem.actual_hours)}</div></div>
                    <div><div className="text-gray-500">{t('auto.proj.variance')}</div>
                      <div className={`font-medium tabular-nums ${variance > 0 ? 'text-[#AA0000]' : variance < 0 ? 'text-green-700' : ''}`}>{formatHMSigned(variance)}</div>
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 space-y-0.5">
                    {(tItem.planned_start || tItem.planned_end) && (
                      <div>Planned: {fmtDt(tItem.planned_start)} → {fmtDt(tItem.planned_end)}</div>
                    )}
                    {(tItem.n_workers || 0) > 0 && (
                      <div className="flex items-center gap-1"><Users className="h-3 w-3" />{tItem.n_workers} worker(s) logged</div>
                    )}
                    {(tItem.active_timers || []).length > 0 && (
                      <div className="text-blue-700 flex items-center gap-1">
                        <Clock className="h-3 w-3 animate-pulse" />{tItem.active_timers.length} live
                      </div>
                    )}
                    {(damageRefsByTaskId[tItem.id] || []).length > 0 && (
                      <div className="flex flex-wrap gap-1" data-testid={`task-damage-refs-${tItem.id}`}>
                        {(damageRefsByTaskId[tItem.id] || []).map((d) => (
                          <span key={d.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                            <Wrench className="h-2.5 w-2.5" /> Repair for {d.report_number}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex justify-end border-t pt-1">
                    <TaskRowActions task={tItem} onTime={openTimeFor} onPhotos={setPhotosOpen}
                      onEdit={openEdit} onCopy={copyTask} onDelete={delTask} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── Compact: one line each, tap to see everything ── */}
      {view === 'compact' && filtered.length > 0 && (
        <Card><CardContent className="p-0 divide-y" data-testid="tasks-compact-view">
          {filtered.map((tItem) => {
            const variance = (tItem.actual_hours || 0) - (tItem.est_hours || 0);
            const status = STATUS_OPTIONS.find((s) => s.value === tItem.status) || STATUS_OPTIONS[0];
            const isOpen = expandedTasks.has(tItem.id);
            return (
              <div key={tItem.id} className={selected.has(tItem.id) ? 'bg-red-50/30' : ''}>
                <div className="flex items-center gap-2 p-2">
                  <Checkbox checked={selected.has(tItem.id)} onCheckedChange={() => toggleOne(tItem.id)}
                    aria-label={`Select ${tItem.name}`} data-testid={`task-select-${tItem.id}`} />
                  <button type="button" className="flex flex-1 min-w-0 items-center gap-2 text-left"
                    onClick={() => toggleExpanded(tItem.id)} data-testid={`task-compact-toggle-${tItem.id}`}>
                    {isOpen ? <ChevronDown className="h-4 w-4 shrink-0 text-gray-400" /> : <ChevronRight className="h-4 w-4 shrink-0 text-gray-400" />}
                    <span className="truncate font-medium">{tItem.name}</span>
                    {tItem.location && <span className="hidden sm:inline truncate text-xs text-[#AA0000]">{tItem.location}</span>}
                  </button>
                  <span className="shrink-0 text-xs tabular-nums text-gray-600">
                    {formatHM(tItem.actual_hours)} / {formatHM(tItem.est_hours)}
                  </span>
                  <span className={`shrink-0 px-1.5 py-0.5 rounded text-xs ${status.color}`}>{status.label}</span>
                </div>
                {isOpen && (
                  <div className="px-3 pb-3 pt-0 space-y-2 text-sm" data-testid={`task-compact-detail-${tItem.id}`}>
                    {tItem.description && <div className="text-xs text-gray-600 break-words">{tItem.description}</div>}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                      <div><div className="text-gray-500">{t('auto.proj.trade')}</div><div>{cap(tItem.trade) || '—'}</div></div>
                      <div><div className="text-gray-500">{t('auto.proj.category')}</div><div>{(CATEGORY_OPTIONS.find((c) => c.value === tItem.category) || CATEGORY_OPTIONS[0]).label}</div></div>
                      <div><div className="text-gray-500">{t('auto.proj.priority')}</div><div>{(PRIORITY_OPTIONS.find((x) => x.value === (tItem.priority || 'normal')) || PRIORITY_OPTIONS[2]).label}</div></div>
                      <div><div className="text-gray-500">{t('auto.proj.quantity')}</div><div className="tabular-nums">{tItem.quantity || 1}</div></div>
                      <div><div className="text-gray-500">{t('auto.proj.location')}</div><div className="break-words">{tItem.location || '—'}</div></div>
                      <div><div className="text-gray-500">{t('auto.proj.planned')}</div><div>{fmtDt(tItem.planned_start)} → {fmtDt(tItem.planned_end)}</div></div>
                      <div><div className="text-gray-500">{t('auto.proj.estActual')}</div><div className="tabular-nums">{formatHM(tItem.est_hours)} / {formatHM(tItem.actual_hours)}</div></div>
                      <div><div className="text-gray-500">{t('auto.proj.variance')}</div>
                        <div className={`tabular-nums ${variance > 0 ? 'text-[#AA0000]' : variance < 0 ? 'text-green-700' : ''}`}>{formatHMSigned(variance)}</div>
                      </div>
                    </div>
                    {tItem.notes && <div className="text-xs text-gray-600 break-words">Notes: {tItem.notes}</div>}
                    <div className="flex items-center justify-between gap-2">
                      <button onClick={() => cycleStatus(tItem)} title={t('auto.proj.clickToCycleStatus')}
                        className={`px-1.5 py-0.5 rounded text-xs ${status.color}`}
                        data-testid={`task-status-cycle-${tItem.id}`}>{status.label}</button>
                      <TaskRowActions task={tItem} onTime={openTimeFor} onPhotos={setPhotosOpen}
                        onEdit={openEdit} onCopy={copyTask} onDelete={delTask} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </CardContent></Card>
      )}

      {view === 'table' && filtered.length > 0 && (
      <Card>
        <CardContent className="p-0">
          <ScrollX testId="tasks-table-scroll" barClassName="px-2">
          <table className="w-full min-w-[720px] text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="p-2 w-8">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll}
                    aria-label={t('auto.proj.selectAll')}
                    className={someSelected && !allSelected ? 'data-[state=unchecked]:bg-gray-300' : ''}
                    data-testid="task-select-all" />
                </th>
                <th className="text-left p-2 min-w-[160px]">{t('auto.proj.task')}</th>
                {cols.trade && <th className="text-left p-2 hidden md:table-cell w-[72px]">{t('auto.proj.trade')}</th>}
                <th className="text-left p-2 w-[72px]">{t('auto.proj.cat')}</th>
                {cols.priority && <th className="text-left p-2 hidden md:table-cell w-[84px]">{t('auto.proj.priority')}</th>}
                {cols.planned && <th className="text-left p-2 hidden lg:table-cell whitespace-nowrap w-[120px]">{t('auto.proj.planned')}</th>}
                {cols.qty && <th className="text-right p-2 hidden md:table-cell w-[40px]">{t('auto.proj.qty')}</th>}
                <th className="text-right p-2 w-[68px]">{t('auto.proj.est')}</th>
                <th className="text-right p-2 w-[68px]">{t('auto.proj.act')}</th>
                {cols.variance && <th className="text-right p-2 hidden md:table-cell w-[76px]">{t('auto.proj.var')}</th>}
                <th className="text-left p-2 w-[88px]">{t('auto.proj.status')}</th>
                {cols.active && <th className="text-left p-2 hidden lg:table-cell w-[56px]">{t('auto.proj.active')}</th>}
                <th className="text-right p-2 w-[88px]">{t('auto.proj.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((tItem) => {
                const variance = (tItem.actual_hours || 0) - (tItem.est_hours || 0);
                const status = STATUS_OPTIONS.find((s) => s.value === tItem.status) || STATUS_OPTIONS[0];
                const cat = CATEGORY_OPTIONS.find((c) => c.value === tItem.category) || CATEGORY_OPTIONS[0];
                const activeTimers = tItem.active_timers || [];
                const checked = selected.has(tItem.id);
                return (
                  <tr key={tItem.id} className={`border-b hover:bg-gray-50 ${checked ? 'bg-red-50/30' : ''}`}>
                    <td className="p-2 align-top">
                      <Checkbox checked={checked} onCheckedChange={() => toggleOne(tItem.id)}
                        aria-label={`Select ${tItem.name}`}
                        data-testid={`task-select-${tItem.id}`} />
                    </td>
                    <td className="p-2 align-top cursor-pointer" onClick={() => openEdit(tItem)} data-testid={`task-row-open-${tItem.id}`}>
                      <div className="font-medium break-words">
                        {tItem.name}
                        {isLocalId(tItem.id) && (
                          <span className="ml-1.5 align-middle inline-block">
                            <PendingSyncBadge testId={`task-pending-${tItem.id}`} />
                          </span>
                        )}
                      </div>
                      {(damageRefsByTaskId[tItem.id] || []).length > 0 && (
                        <div className="text-[10px] mt-0.5 flex flex-wrap gap-1" data-testid={`task-damage-refs-${tItem.id}`}>
                          {(damageRefsByTaskId[tItem.id] || []).map((d) => (
                            <span key={d.id} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                              <Wrench className="h-2.5 w-2.5" /> Repair for {d.report_number}
                            </span>
                          ))}
                        </div>
                      )}
                      {tItem.location && (
                        <div className="text-xs text-[#AA0000] font-medium flex items-center gap-1 mt-0.5">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          <span className="break-words">{tItem.location}</span>
                        </div>
                      )}
                      {tItem.description && <div className="text-xs text-gray-500 break-words">{tItem.description}</div>}
                      {/* Mobile-only meta line: trade · qty · planned · live */}
                      <div className="md:hidden mt-1 text-[10px] text-gray-500 flex flex-wrap gap-x-2 gap-y-0.5">
                        {tItem.trade && <span>{cap(tItem.trade)}</span>}
                        {(tItem.quantity || 1) !== 1 && <span>×{tItem.quantity}</span>}
                        {(tItem.planned_start || tItem.planned_end) && (
                          <span className="whitespace-nowrap">{fmtDt(tItem.planned_start)}</span>
                        )}
                        {variance !== 0 && (
                          <span className={variance > 0 ? 'text-[#AA0000] font-medium' : 'text-green-700'}>
                            Δ {formatHMSigned(variance)}
                          </span>
                        )}
                        {activeTimers.length > 0 && (
                          <span className="text-blue-700 flex items-center gap-0.5">
                            <Clock className="h-2.5 w-2.5 animate-pulse" />{activeTimers.length} live
                          </span>
                        )}
                      </div>
                    </td>
                    {cols.trade && <td className="p-2 hidden md:table-cell align-top">{cap(tItem.trade) || '—'}</td>}
                    <td className="p-2 align-top"><span className={`px-1.5 py-0.5 rounded text-xs ${cat.color}`}>{cat.label}</span></td>
                    {cols.priority && (
                      <td className="p-2 hidden md:table-cell align-top">
                        {(() => {
                          const p = PRIORITY_OPTIONS.find((x) => x.value === (tItem.priority || 'normal')) || PRIORITY_OPTIONS[2];
                          return <span className={`px-1.5 py-0.5 rounded text-xs ${p.color}`}>{p.label}</span>;
                        })()}
                      </td>
                    )}
                    {cols.planned && (
                      <td className="p-2 text-xs whitespace-nowrap hidden lg:table-cell align-top">
                        {tItem.planned_start || tItem.planned_end ? (
                          <>
                            <div>{fmtDt(tItem.planned_start)}</div>
                            <div className="text-gray-500">→ {fmtDt(tItem.planned_end)}</div>
                          </>
                        ) : <span className="text-gray-400">—</span>}
                      </td>
                    )}
                    {cols.qty && <td className="p-2 text-right tabular-nums hidden md:table-cell align-top">{tItem.quantity || 1}</td>}
                    <td className="p-2 text-right tabular-nums align-top whitespace-nowrap">{formatHM(tItem.est_hours)}</td>
                    <td className="p-2 text-right tabular-nums align-top whitespace-nowrap">{formatHM(tItem.actual_hours)}</td>
                    {cols.variance && (
                      <td className={`p-2 text-right tabular-nums hidden md:table-cell align-top whitespace-nowrap ${variance > 0 ? 'text-[#AA0000] font-medium' : variance < 0 ? 'text-green-700' : ''}`}>
                        {formatHMSigned(variance)}
                      </td>
                    )}
                    <td className="p-2 align-top">
                      <button
                        onClick={() => cycleStatus(tItem)}
                        className={`px-1.5 py-0.5 rounded text-xs hover:ring-2 hover:ring-[#AA0000] hover:ring-offset-1 transition-all ${status.color}`}
                        title={t('auto.proj.clickToCycleStatus')}
                        data-testid={`task-status-cycle-${tItem.id}`}
                      >
                        {status.label}
                      </button>
                    </td>
                    {cols.active && (
                      <td className="p-2 hidden lg:table-cell align-top">
                        {activeTimers.length > 0 ? (
                          <span className="text-xs text-blue-700 flex items-center gap-1">
                            <Clock className="h-3 w-3 animate-pulse" />
                            {activeTimers.length} live
                          </span>
                        ) : <span className="text-xs text-gray-400">—</span>}
                      </td>
                    )}
                    <td className="p-1 text-right whitespace-nowrap align-top">
                      <TaskRowActions task={tItem} onTime={openTimeFor} onPhotos={setPhotosOpen}
                        onEdit={openEdit} onCopy={copyTask} onDelete={delTask} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </ScrollX>
        </CardContent>
      </Card>
      )}

      {/* Create/Edit Task Dialog */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-[1100px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? t('tasks.editTask') : t('tasks.addTask')}</DialogTitle>
            <DialogDescription>{editing ? t('auto.proj.updateTheTaskDetails') : t('auto.proj.pickATemplateOrCreateAd')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {!editing && (
              <div>
                <Label>{t('auto.proj.fromTemplateOptional')}</Label>
                <Select value={form.template_id || '__none__'} onValueChange={(v) => onTemplatePick(v === '__none__' ? '' : v)}>
                  <SelectTrigger data-testid="task-template-picker"><SelectValue placeholder={t('auto.proj.none')} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t('auto.proj.noneAdHoc')}</SelectItem>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name} {t.trade ? `· ${t.trade}` : ''} · {t.est_hours}h
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>{t('auto.proj.name2')}</Label>
              <Input value={form.name || ''} onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value  }))} data-testid="task-name-input" />
            </div>
            <div>
              <Label>{t('auto.proj.description')}</Label>
              <Input value={form.description || ''} onChange={(e) => setForm(prev => ({ ...prev, description: e.target.value  }))} />
            </div>
            <div>
              <Label>{t('auto.proj.location')} <span className="text-gray-400 text-xs ml-1">{t('auto.proj.partOfInstallationCabinetLiftShaft')}</span></Label>
              <Input
                value={form.location || ''}
                onChange={(e) => setForm(prev => ({ ...prev, location: e.target.value  }))}
                placeholder="e.g. Cabinet -Q02F, Lift shaft 3, DB-MCC-01"
                data-testid="task-location-input"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              <div className="min-w-0">
                <Label>{t('auto.proj.trade')}</Label>
                <Select value={form.trade || '__none__'} onValueChange={(v) => setForm(prev => ({ ...prev, trade: v === '__none__' ? '' : v  }))}>
                  <SelectTrigger data-testid="task-trade-select"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">{t('auto.proj.none')}</SelectItem>
                    {trades.map((t) => <SelectItem key={t} value={t}>{cap(t)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-0">
                <Label>{t('auto.proj.category')}</Label>
                <Select value={form.category || 'standard'} onValueChange={(v) => setForm(prev => ({ ...prev, category: v  }))}>
                  <SelectTrigger data-testid="task-category-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORY_OPTIONS.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-0">
                <Label>{t('auto.proj.quantity')}</Label>
                <Input type="number" step="0.5" value={form.quantity || 1} onChange={(e) => setForm(prev => ({ ...prev, quantity: e.target.value  }))} />
              </div>
              <div className="min-w-0 sm:col-span-2 lg:col-span-1">
                <Label>{t('auto.proj.plannedStart')}</Label>
                <Input type="datetime-local" className="w-full" value={form.planned_start || ''} onChange={(e) => setForm(prev => ({ ...prev, planned_start: e.target.value  }))} data-testid="task-planned-start" />
              </div>
              <div className="min-w-0 sm:col-span-2 lg:col-span-1">
                <Label>{t('auto.proj.plannedEnd3')}</Label>
                <Input type="datetime-local" className="w-full" value={form.planned_end || ''} onChange={(e) => setForm(prev => ({ ...prev, planned_end: e.target.value  }))} data-testid="task-planned-end" />
              </div>
              <div className="min-w-0">
                <Label>{t('auto.proj.estHours')} <span className="text-xs text-gray-500">{t('auto.proj.perWorkerPerUnit')}</span></Label>
                <Input
                  type="number"
                  step="0.25"
                  value={form.est_hours_unit ?? 0}
                  onChange={(e) => setForm(prev => ({ ...prev, est_hours_unit: e.target.value  }))}
                  data-testid="task-est-hours-unit"
                />
                <div className="text-xs text-gray-500 mt-1" data-testid="task-est-hours-total-preview">
                  Total: <b>{
                    formatHM(
                      (parseFloat(form.est_hours_unit) || 0)
                      * (parseFloat(form.quantity) || 1)
                      * (parseInt(form.est_workers) || 1)
                    )
                  }</b>
                  <span className="text-gray-400"> = {formatHM(parseFloat(form.est_hours_unit) || 0)} × {parseFloat(form.quantity) || 1} × {parseInt(form.est_workers) || 1} worker(s)</span>
                </div>
              </div>
              <div className="min-w-0">
                <Label>{t('auto.proj.estWorkers')}</Label>
                <Input type="number" min="1" value={form.est_workers || 1} onChange={(e) => setForm(prev => ({ ...prev, est_workers: e.target.value  }))} />
              </div>
              <div className="min-w-0">
                <Label>{t('auto.proj.priority')}</Label>
                <Select value={form.priority || 'normal'} onValueChange={(v) => setForm(prev => ({ ...prev, priority: v  }))}>
                  <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PRIORITY_OPTIONS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            {editing && (
              <>
                <div>
                  <Label>{t('auto.proj.status')}</Label>
                  <Select value={form.status || 'not_started'} onValueChange={(v) => setForm(prev => ({ ...prev, status: v  }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {STATUS_OPTIONS.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('auto.proj.notes')}</Label>
                  <Input value={form.notes || ''} onChange={(e) => setForm(prev => ({ ...prev, notes: e.target.value  }))} />
                </div>
              </>
            )}
            {form.category === 'extra' && (
              <div className="text-xs text-orange-700 bg-orange-50 border border-orange-200 rounded p-2 flex gap-2">
                <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span>{t('auto.proj.thisIsAn')} <b>{t('auto.proj.extra')}</b> {t('auto.proj.outOfScopeTaskUseThe')}</span>
              </div>
            )}
          </div>
          <FloatingSaveButton onClick={save} label={t('auto.proj.saveTask')} testId="task-floating-save-btn" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="dialog-cancel-btn">{t('auto.proj.cancel')}</Button>
            <Button onClick={save} className="bg-[#AA0000] hover:bg-[#880000]" data-testid="save-task-btn">{t('auto.proj.save')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Time entries dialog */}
      <Dialog open={!!timeOpen} onOpenChange={(o) => !o && setTimeOpen(null)}>
        <DialogContent className="max-w-[95vw] sm:max-w-[1100px] max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Time Entries — {timeOpen?.name}</DialogTitle>
            <DialogDescription>{t('auto.proj.logHoursManuallyOrUseThe')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Card><CardContent className="p-3 space-y-2">
              <div className="font-semibold text-sm">{t('auto.proj.quickLogTimer')}</div>
              <div className="grid grid-cols-2 gap-2 items-end sm:grid-cols-12">
                <div className="col-span-2 sm:col-span-5">
                  <Label className="text-xs">{t('auto.proj.employee')}</Label>
                  <Select value={entryForm.employee_id || ''} onValueChange={(v) => setEntryForm(prev => ({ ...prev, employee_id: v  }))}>
                    <SelectTrigger><SelectValue placeholder={t('auto.proj.selectEmployee')} /></SelectTrigger>
                    <SelectContent>
                      {teamEmployees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name || e.full_name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-1 sm:col-span-2">
                  <Label className="text-xs">{t('auto.proj.hours')}</Label>
                  <Input type="number" step="0.25" value={entryForm.hours || ''} onChange={(e) => setEntryForm(prev => ({ ...prev, hours: e.target.value  }))} placeholder="e.g. 2.5" />
                </div>
                <div className="col-span-2 sm:col-span-3">
                  <Label className="text-xs">{t('auto.proj.date')}</Label>
                  <Input type="date" className="min-w-0 pr-2" value={entryForm.work_date || ''} onChange={(e) => setEntryForm(prev => ({ ...prev, work_date: e.target.value  }))} />
                </div>
                <div className="col-span-2 flex gap-1 sm:col-span-2">
                  <Button onClick={addManualEntry} className="bg-[#AA0000] hover:bg-[#880000] flex-1" size="sm" data-testid="log-hours-btn">{t('auto.proj.log')}</Button>
                </div>
                <div className="col-span-2 sm:col-span-12 flex flex-wrap items-center gap-2 pt-1">
                  <label className="flex items-center gap-1.5 text-xs text-gray-700 px-2 py-1 border rounded cursor-pointer">
                    <Checkbox
                      checked={!!entryForm.multiply}
                      onCheckedChange={(v) => setEntryForm(prev => ({ ...prev, multiply: !!v }))}
                      data-testid="time-multiply-quantity"
                    />
                    × task quantity ({timeOpen?.quantity || 1})
                  </label>
                  <span className="text-xs text-gray-500" data-testid="time-effective-preview">
                    Logs <b>{formatHM((parseFloat(entryForm.hours) || 0) * (entryForm.multiply ? (parseFloat(timeOpen?.quantity) || 1) : 1))}</b>
                    {entryForm.multiply && (parseFloat(timeOpen?.quantity) || 1) !== 1
                      ? ` (${formatHM(parseFloat(entryForm.hours) || 0)} × ${parseFloat(timeOpen?.quantity) || 1})`
                      : ''}
                  </span>
                </div>
                <div className="col-span-2 sm:col-span-12 flex flex-wrap gap-2 pt-1">
                  <Button size="sm" variant="outline" disabled={!entryForm.employee_id} onClick={() => startTimer(timeOpen, entryForm.employee_id)} data-testid="start-timer-btn">
                    <Play className="h-3 w-3 mr-1" />{t('auto.proj.startTimer')}
                  </Button>
                  <Button size="sm" variant="outline" disabled={!entryForm.employee_id} onClick={() => stopTimer(timeOpen, entryForm.employee_id)} data-testid="stop-timer-btn">
                    <Square className="h-3 w-3 mr-1" />{t('auto.proj.stopTimer')}
                  </Button>
                  <Input className="flex-1 min-w-[160px]" placeholder={t('auto.proj.notesOptional')} value={entryForm.notes || ''} onChange={(e) => setEntryForm(prev => ({ ...prev, notes: e.target.value  }))} />
                </div>
              </div>
            </CardContent></Card>

            <div className="max-h-80 overflow-auto border rounded">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 sticky top-0"><tr>
                  <th className="text-left p-2">{t('auto.proj.date')}</th>
                  <th className="text-left p-2">{t('auto.proj.employee')}</th>
                  <th className="text-right p-2">{t('auto.proj.hours')}</th>
                  <th className="text-left p-2">{t('auto.proj.notes')}</th>
                  <th className="p-2"></th>
                </tr></thead>
                <tbody>
                  {entries.length === 0 && <tr><td colSpan={5} className="p-4 text-center text-gray-500">{t('auto.proj.noEntries')}</td></tr>}
                  {entries.map((e) => {
                    const isLive = e.is_active === true && e.end_at == null;
                    return (
                      <tr key={e.id} className={`border-b ${isLive ? 'bg-red-50/40' : ''}`}>
                        <td className="p-2"><DateText value={e.work_date} /></td>
                        <td className="p-2">{empByName[e.employee_id] || e.employee_id}</td>
                        <td className="p-2 text-right tabular-nums">
                          {isLive ? <span className="text-[#AA0000] flex items-center gap-1 justify-end"><Clock className="h-3 w-3 animate-pulse" />running</span> : (
                            <>
                              {formatHM(e.hours)}
                              {(e.quantity || 1) !== 1 && (
                                <div className="text-[10px] text-gray-500">{formatHM(e.hours_entered)} × {e.quantity}</div>
                              )}
                            </>
                          )}
                        </td>
                        <td className="p-2 text-gray-600">{e.notes || ''}</td>
                        <td className="p-2 text-right whitespace-nowrap">
                          {isLive && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => stopTimer(timeOpen, e.employee_id)}
                              className="text-[#AA0000] border-[#AA0000] hover:bg-[#AA0000] hover:text-white mr-1"
                              data-testid={`stop-timer-row-${e.id}`}
                            >
                              <Square className="h-3 w-3 mr-1" />{t('auto.proj.stop')}
                            </Button>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => delEntry(e.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => setTimeOpen(null)} variant="outline" data-testid="dialog-close-btn">{t('auto.proj.close')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bill Extras to Customer Dialog */}
      <Dialog open={billOpen} onOpenChange={setBillOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-[1100px] max-h-[92vh] overflow-y-auto" data-testid="bill-extras-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Receipt className="h-5 w-5 text-[#AA0000]" />
              {t('auto.proj.billExtrasToCustomer')}
            </DialogTitle>
            <DialogDescription>
              Hours are billed at the <b>{t('auto.proj.projectHourlyRate')}</b> (not the engineer's pay rate).
            </DialogDescription>
          </DialogHeader>
          {billSummary && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Stat label="Project Hourly Rate" value={`${billSummary.hourly_rate?.toFixed(2) || '0.00'} ${billSummary.currency}`} valueClass="text-[#AA0000]" />
                <Stat label={t('auto.proj.extraTasks')} value={billSummary.tasks?.length || 0} />
                <Stat label={t('auto.proj.totalHours')} value={formatHM(billSummary.totals?.hours)} />
                <Stat label="Total Amount" value={`${(billSummary.totals?.amount || 0).toFixed(2)} ${billSummary.currency}`} valueClass="text-[#AA0000] font-bold" />
              </div>

              {(!billSummary.hourly_rate || billSummary.hourly_rate <= 0) && (
                <div className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded p-2 flex gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span>{t('auto.proj.projectHourlyRateIsNotSet')} <b>{t('auto.proj.projectDetails')}</b> {t('auto.proj.andSet')} <b>{t('auto.proj.hourlyRate')}</b> {t('auto.proj.beforeBilling')}</span>
                </div>
              )}

              <div className="border rounded max-h-60 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0"><tr>
                    <th className="p-2 w-8"></th>
                    <th className="text-left p-2">{t('auto.proj.task')}</th>
                    <th className="text-left p-2">{t('auto.proj.trade')}</th>
                    <th className="text-right p-2">{t('auto.proj.hours')}</th>
                    <th className="text-right p-2">{t('auto.proj.rate')}</th>
                    <th className="text-right p-2">{t('auto.proj.amount')}</th>
                  </tr></thead>
                  <tbody>
                    {(billSummary.tasks || []).length === 0 && (
                      <tr><td colSpan={6} className="p-4 text-center text-gray-500">{t('auto.proj.noExtraTasksWithLoggedHours')}</td></tr>
                    )}
                    {(billSummary.tasks || []).map((tItem) => {
                      const isSel = billForm.task_ids?.has(tItem.task_id);
                      return (
                        <tr key={tItem.task_id} className={`border-b ${isSel ? 'bg-red-50/30' : ''}`}>
                          <td className="p-2">
                            <Checkbox
                              checked={isSel}
                              onCheckedChange={() => {
                                setBillForm((f) => {
                                  const s = new Set(f.task_ids || []);
                                  if (s.has(tItem.task_id)) s.delete(tItem.task_id); else s.add(tItem.task_id);
                                  return { ...f, task_ids: s };
                                });
                              }}
                              data-testid={`bill-extras-select-${tItem.task_id}`}
                            />
                          </td>
                          <td className="p-2">
                            <div className="font-medium">{tItem.task_name}</div>
                            {tItem.uses_estimate && <div className="text-xs text-orange-700">{t('auto.proj.usingEstimatedHoursNoTimeEntries')}</div>}
                          </td>
                          <td className="p-2">{cap(tItem.trade) || '—'}</td>
                          <td className="p-2 text-right tabular-nums">{formatHM(tItem.hours)}</td>
                          <td className="p-2 text-right tabular-nums">{tItem.rate?.toFixed(2)}</td>
                          <td className="p-2 text-right tabular-nums font-medium">{tItem.amount?.toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <Label>{t('auto.proj.issueDate')}</Label>
                  <Input type="date" className="min-w-0 pr-2" value={billForm.issue_date || ''} onChange={(e) => setBillForm(prev => ({ ...prev, issue_date: e.target.value  }))} data-testid="bill-extras-issue-date" />
                </div>
                <div>
                  <Label>{t('auto.proj.saleDate')}</Label>
                  <Input type="date" className="min-w-0 pr-2" value={billForm.sale_date || ''} onChange={(e) => setBillForm(prev => ({ ...prev, sale_date: e.target.value  }))} />
                </div>
                <div>
                  <Label>{t('auto.proj.dueDate')}</Label>
                  <Input type="date" className="min-w-0 pr-2" value={billForm.due_date || ''} onChange={(e) => setBillForm(prev => ({ ...prev, due_date: e.target.value  }))} />
                </div>
              </div>
              <div>
                <Label>{t('auto.proj.notes')}</Label>
                <Input value={billForm.notes || ''} onChange={(e) => setBillForm(prev => ({ ...prev, notes: e.target.value  }))} placeholder={`Extra works on ${projectName || 'this project'}`} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setBillOpen(false)} data-testid="dialog-cancel-btn">{t('auto.proj.cancel')}</Button>
            <Button
              onClick={submitBillExtras}
              className="bg-[#AA0000] hover:bg-[#880000]"
              disabled={!billSummary?.hourly_rate || billSummary.hourly_rate <= 0}
              data-testid="bill-extras-submit-btn"
            >
              <Receipt className="h-4 w-4 mr-1" />{t('auto.proj.createInvoice')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-task Photo Gallery dialog */}
      <Dialog open={!!photosOpen} onOpenChange={(o) => !o && setPhotosOpen(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Photos — {photosOpen?.name}</DialogTitle>
            <DialogDescription>
              Attach evidence photos to this task. Photos are compressed on upload
              and included automatically in the Photo Evidence appendix when you
              export the task report PDF.
            </DialogDescription>
          </DialogHeader>
          {photosOpen && (
            <EntityPhotoGallery
              entityType="project_task"
              entityId={photosOpen.id}
              onCountChange={onPhotoCountChange}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Task-completion signature dialog — triggered when the
          status cycles into "completed". Skipping is allowed for
          operators who prefer typed sign-off. */}
      <TaskCompleteSignatureDialog
        task={completingTask}
        onCancel={() => setCompletingTask(null)}
        onSubmit={(sig) => finalizeTaskCompletion(completingTask, sig)}
      />
    </div>
  );
};

// ── Task-complete signature dialog ────────────────────────────────
// Handwritten sign-off for the moment a task is marked complete.
// Optional — the "Skip" button commits the status change without a
// signature so casual completions still work in one click.
const TaskCompleteSignatureDialog = ({ task, onCancel, onSubmit }) => {
  const { t } = useTranslation();
  const { savedSignature, saveAsDefault } = useDefaultSignature();
  const [sig, setSig] = useState(null);
  useEffect(() => { setSig(null); }, [task?.id]);
  if (!task) return null;
  return (
    <Dialog open={!!task} onOpenChange={(v) => { if (!v) onCancel(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><PenLine className="w-5 h-5" />{t('auto.proj.completeTask')}</DialogTitle>
          <DialogDescription>
            Optionally sign off <b>{task.name || task.title || 'this task'}</b>. Skip if you'd rather record it without a handwritten signature.
          </DialogDescription>
        </DialogHeader>
        <SignaturePad
          value={sig}
          onChange={setSig}
          height={130}
          testIdPrefix={`task-sig-${task.id}`}
          savedSignature={savedSignature}
          onSaveDefault={async (dataUrl) => {
            const ok = await saveAsDefault(dataUrl);
            toast[ok ? 'success' : 'error'](ok ? t('auto.proj.savedAsYourDefaultSignature') : t('auto.proj.failedToSaveDefault'));
          }}
        />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onCancel} data-testid="task-complete-cancel">{t('auto.proj.cancel')}</Button>
          <Button variant="outline" onClick={() => onSubmit(null)} data-testid="task-complete-skip">Skip &amp; complete</Button>
          <Button onClick={() => onSubmit(sig)} disabled={!sig} data-testid="task-complete-sign">
            <PenLine className="h-3.5 w-3.5 mr-1" /> Sign &amp; complete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TaskRowActions = ({ task, onTime, onPhotos, onEdit, onCopy, onDelete }) => (
  <div className="inline-flex items-center">
    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onTime(task)} title="Time entries" data-testid={`task-time-btn-${task.id}`}>
      <Clock className="h-4 w-4" />
    </Button>
    <Button size="sm" variant="ghost" className="h-7 w-7 p-0 relative" onClick={() => onPhotos(task)} title="Photos" data-testid={`task-photos-btn-${task.id}`}>
      <Camera className="h-4 w-4" />
      {(task.photo_count || 0) > 0 && (
        <span className="absolute -top-0.5 -right-0.5 bg-[#AA0000] text-white text-[9px] rounded-full h-3.5 w-3.5 flex items-center justify-center tabular-nums">
          {task.photo_count}
        </span>
      )}
    </Button>
    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onEdit(task)} title="Edit" data-testid={`task-edit-btn-${task.id}`}><Pencil className="h-4 w-4" /></Button>
    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onCopy(task)} title="Copy & edit" data-testid={`task-copy-btn-${task.id}`}><Copy className="h-4 w-4" /></Button>
    <Button size="sm" variant="ghost" className="h-7 w-7 p-0" onClick={() => onDelete(task)} title="Delete" data-testid={`task-delete-btn-${task.id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>
  </div>
);

const Stat = ({ label, value, valueClass = '' }) => (
  <Card><CardContent className="p-3">
    <div className="text-xs text-gray-500">{label}</div>
    <div className={`text-xl font-semibold tabular-nums ${valueClass}`}>{value}</div>
  </CardContent></Card>
);

export default ProjectTasks;
